<template>
    <div class="modal relative flex items-center justify-center">
        <Button variant="tertiary" class="button" :class="buttonClass" @click="toggleModal()">
            <div v-if="iconClass" class="flex">
                <!-- text-inherit: mono mask icons follow the button ink (incl. the
                     tertiary hover ink flip) instead of their utility colour pin;
                     layered icons (i-bell) don't read currentColor and keep theirs. -->
                <i class="text-inherit" :class="iconClass"></i>
            </div>
            {{ buttonText }}
        </Button>
        <Transition v-if="transition">
            <div
                v-if="showModal"
                id="modal"
                class="mt-xs p-sm absolute top-full z-50 rounded-md border border-(--border-subtle) bg-(--overlay-surface)"
                :class="{
                    'left-1/2 -translate-x-1/2': placement === 'center',
                    'left-0': placement === 'left',
                    'right-0': placement === 'right',
                    'bottom-full': placement === 'top',
                    'top-full': placement === 'bottom',
                    'top-full left-0': placement === 'bottom-left',
                }"
            >
                <slot />
            </div>
        </Transition>
        <div
            v-else-if="!transition && showModal"
            id="modal"
            class="my-xs p-sm absolute z-50 rounded-md border border-(--border-subtle) bg-(--overlay-surface)"
            :class="{
                'left-1/2 -translate-x-1/2': placement === 'center',
                'left-0': placement === 'left',
                'right-0': placement === 'right',
                'bottom-full': placement === 'top',
                'top-full': placement === 'bottom',
                'top-full left-0': placement === 'bottom-left',
            }"
        >
            <slot />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button';
import { ref, watch, type PropType } from 'vue';

defineProps({
    buttonText: { type: String, required: false },
    iconClass: { type: String, required: false },
    buttonClass: { type: String, required: false },
    placement: {
        type: String as PropType<'left' | 'center' | 'right' | 'top' | 'bottom' | 'bottom-left'>,
        default: 'right',
    },
    transition: { type: Boolean, required: false, default: false },
});

const showModal = ref(false);

watch(showModal, (value) => {
    if (value) {
        document.addEventListener('click', clickOutside);
    }
});

const clickOutside = (event: MouseEvent) => {
    if (
        !(event.target as HTMLElement).closest('.modal') &&
        !(event.target as HTMLElement).closest('.ignore-click-outside')
    ) {
        toggleModal();
    }
};

const toggleModal = () => {
    showModal.value = !showModal.value;
    if (!showModal.value) {
        document.removeEventListener('click', clickOutside);
    }
};

defineExpose({ toggleModal });
</script>

<style scoped>
@reference '@/assets/styles/default.css';
#modal {
    box-shadow: 0px 12px 26px 0px rgba(15, 43, 128, 0.06);
}

.button {
    @apply border-2 border-(--btn-outline-fg) bg-(--surface-1) text-(--link-default);

    &:hover {
        @apply bg-(--btn-primary-bg) text-(--text-on-accent);

        /* Icons take the button ink on the accent fill: mono mask icons via
           currentColor (text-inherit on the trigger icon), layered icons
           (i-bell) via their pseudo layers. Replaces the pre-3090
           brightness/invert whitening, which forced white in both themes —
           wrong on dark's pale-blue fill (ink is dark there). */
        i::before,
        i::after {
            background-color: currentColor;
        }
    }
}

.v-enter-active,
.v-leave-active {
    transition: opacity ease 0.3s;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
